import { normalizeUsbcId } from "./bowlerMatchKeys";
import { isDueCartLine } from "./signupPaymentQuote";
import { toCents } from "../utils/currencyUtils";

const storageKey = (tournamentId) => `bta-signup-cart:${tournamentId}`;

const emptyCart = () => ({ lines: [] });

const canUseStorage = () =>
  typeof window !== "undefined" && window.sessionStorage;

const normalizeName = (name) => String(name || "").trim().toLowerCase();

export const bowlersOnLine = (line) => {
  const people = [line?.primaryBowler, ...(line?.teammates || [])].filter(
    Boolean
  );
  return people.filter((person) => normalizeName(person.name));
};

const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

const identityKey = (person) => {
  const usbc = normalizeUsbcId(person?.usbcId || person?.usbcID);
  if (usbc) return `usbc:${usbc}`;
  const email = normalizeEmail(person?.email);
  if (email) return `email:${email}`;
  return null;
};

export const sameBowler = (left, right) => {
  const leftKey = identityKey(left);
  const rightKey = identityKey(right);
  return Boolean(leftKey && rightKey && leftKey === rightKey);
};

export const loadSignupCart = (tournamentId) => {
  if (!tournamentId || !canUseStorage()) return emptyCart();
  try {
    const raw = window.sessionStorage.getItem(storageKey(tournamentId));
    if (!raw) return emptyCart();
    const parsed = JSON.parse(raw);
    return {
      lines: Array.isArray(parsed?.lines) ? parsed.lines : [],
    };
  } catch (error) {
    return emptyCart();
  }
};

export const saveSignupCart = (tournamentId, cart) => {
  if (!tournamentId || !canUseStorage()) return emptyCart();
  const next = { lines: cart?.lines || [] };
  window.sessionStorage.setItem(storageKey(tournamentId), JSON.stringify(next));
  return next;
};

const eventIdOf = (row) => row?.eventID || row?.event?.id;
const squadIdOf = (row) => row?.squadID || row?.squad?.id;

export const signupAlreadyOnEventSquad = (
  signups = [],
  eventId,
  squadId
) => {
  if (!eventId || !squadId) return false;
  return (signups || []).some(
    (signup) =>
      eventIdOf(signup) === eventId && squadIdOf(signup) === squadId
  );
};

export const namesAlreadyOnEventSquad = (
  signups = [],
  eventId,
  squadId,
  names = []
) => {
  const wanted = new Set(
    (names || []).map((name) => normalizeName(name)).filter(Boolean)
  );
  if (!eventId || !squadId || !wanted.size) return [];
  const found = [];
  (signups || []).forEach((signup) => {
    if (eventIdOf(signup) !== eventId || squadIdOf(signup) !== squadId) {
      return;
    }
    bowlersOnLine(signup).forEach((person) => {
      const key = normalizeName(person.name);
      if (!wanted.has(key)) return;
      if (!found.some((name) => normalizeName(name) === key)) {
        found.push(person.name);
      }
    });
  });
  return found;
};

export const duplicateEventSquadError = (line, cart, existingSignups = []) => {
  const people = bowlersOnLine(line);
  const eventId = line?.event?.id;
  const squadId = line?.squad?.id;
  if (!eventId || !squadId || !people.length) return null;

  const seen = [];
  const check = (person) => {
    if (!person) return;
    const match = people.find((incoming) => sameBowler(incoming, person));
    if (!match) return;
    const label = match.name || person.name;
    if (!seen.some((name) => normalizeName(name) === normalizeName(label))) {
      seen.push(label);
    }
  };

  (cart?.lines || []).forEach((other) => {
    if (other.id === line.id) return;
    if (eventIdOf(other) !== eventId || squadIdOf(other) !== squadId) return;
    bowlersOnLine(other).forEach((person) => check(person));
  });

  existingSignups.forEach((signup) => {
    if (eventIdOf(signup) !== eventId || squadIdOf(signup) !== squadId) return;
    bowlersOnLine(signup).forEach((person) => check(person));
  });

  if (!seen.length) return null;
  return `${seen.join(", ")} is already entered in this event on this squad.`;
};

const newLineId = () =>
  `preview-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const addSignupCartLines = (tournamentId, newLines) => {
  const cart = loadSignupCart(tournamentId);
  const lines = [...cart.lines, ...newLines];
  return saveSignupCart(tournamentId, { lines });
};

export const removeSignupCartLine = (tournamentId, lineId) => {
  const cart = loadSignupCart(tournamentId);
  return saveSignupCart(tournamentId, {
    lines: cart.lines.filter((line) => line.id !== lineId),
  });
};

export const createCartLine = ({
  event,
  squad,
  primaryBowler,
  teammates = [],
  sidepots = {},
  waitlisted = false,
  companionOf = null,
}) => ({
  id: newLineId(),
  event,
  squad,
  primaryBowler,
  teammates,
  sidepots,
  waitlisted,
  companionOf,
  preview: true,
  createdAt: new Date().toISOString(),
});

export const tournamentSignupToCartLine = (signup) => {
  const bowlers = [...(signup.signupBowlers || [])];
  const primary =
    bowlers.find((bowler) => bowler.role === "primary") || bowlers[0] || {};
  const teammates = bowlers.filter((bowler) => bowler !== primary);
  const fees = signup.tournamentEvents?.eventFee || [];
  const entryFee = fees.reduce(
    (sum, fee) => sum + Number(fee.amount || 0),
    0
  );
  const bowlerCount = Math.max(bowlers.length, 1);
  const entryTotal = entryFee * bowlerCount;
  const sidepotTotal = bowlers.reduce(
    (sum, bowler) => sum + Number(bowler.totalSidepotCost || 0),
    0
  );
  const bracketTotal = bowlers.reduce(
    (sum, bowler) => sum + Number(bowler.totalBracketCost || 0),
    0
  );
  const advertisedTotal = entryTotal + sidepotTotal + bracketTotal;
  return {
    id: signup.id,
    event: {
      id: signup.tournamentEvents?.id,
      eventName: signup.tournamentEvents?.eventName,
      entryFee,
    },
    squad: signup.squad,
    primaryBowler: primary,
    teammates,
    waitlisted: signup.status === "waitlisted",
    waitlistPosition:
      signup.status === "waitlisted"
        ? signup.waitlistPosition ?? null
        : null,
    status: signup.status,
    paymentStatus: signup.paymentStatus,
    requiredCancelGroupId: signup.requiredCancelGroupId || null,
    heldUntil: signup.heldUntil || null,
    signupNoticeAt: signup.signupNoticeAt || null,
    tournamentId: signup.tournament?.id,
    entryTotal,
    sidepotTotal,
    bracketTotal,
    advertisedTotal,
    entryCents: toCents(entryTotal),
    sidepotCents: toCents(sidepotTotal),
    bracketCents: toCents(bracketTotal),
    advertisedCents: toCents(advertisedTotal),
  };
};

export const advertisedLineTotal = (line) => {
  if (typeof line?.advertisedTotal === "number") return line.advertisedTotal;
  const bowlerCount =
    1 + (Array.isArray(line?.teammates) ? line.teammates.length : 0);
  const entry = Number(line?.event?.entryFee || 0) * bowlerCount;
  let sidepotTotal = 0;
  Object.values(line?.sidepots || {}).forEach((byEvent) => {
    if (typeof byEvent === "number") {
      sidepotTotal += byEvent || 0;
      return;
    }
    Object.values(byEvent || {}).forEach((amount) => {
      sidepotTotal += Number(amount || 0);
    });
  });
  let bracketTotal = Number(line?.bracketTotal || 0);
  return entry + sidepotTotal + bracketTotal;
};

export { isDueCartLine };

/** Open Cart order: holds and waitlist that have not been closed out. */
export const isOpenCartLine = (line) => {
  if (!line) return false;
  if (line.waitlisted || line.status === "waitlisted") {
    return !line.signupNoticeAt;
  }
  return line.status === "held" || line.paymentStatus === "held";
};

export const dueCartLinesFromMySignups = (data, tournamentId) => {
  const rows = data?.activeSignups || data?.signups || [];
  return rows
    .map(tournamentSignupToCartLine)
    .filter((line) => {
      if (!isDueCartLine(line)) return false;
      if (tournamentId && line.tournamentId !== tournamentId) return false;
      return true;
    });
};

export const openCartLinesFromMySignups = (data, tournamentId) => {
  const rows = data?.activeSignups || data?.signups || [];
  return rows
    .map(tournamentSignupToCartLine)
    .filter((line) => {
      if (!isOpenCartLine(line)) return false;
      if (tournamentId && line.tournamentId !== tournamentId) return false;
      return true;
    });
};

export const countDueCartLines = (data, tournamentId) =>
  dueCartLinesFromMySignups(data, tournamentId).length;

export const countOpenCartLines = (data, tournamentId) =>
  openCartLinesFromMySignups(data, tournamentId).length;

export const cartDueTotal = (cart) =>
  (cart?.lines || [])
    .filter(isDueCartLine)
    .reduce((sum, line) => sum + advertisedLineTotal(line), 0);
